/**
 * Integration Callback Handler
 * Handles OAuth redirects from third-party services
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiLoader, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

export default function IntegrationCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Connecting integration...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const success = searchParams.get('success');
        const error = searchParams.get('error');
        const integrationName = searchParams.get('integration');

        if (error) {
          setStatus('error');
          setMessage(decodeURIComponent(error));
          setTimeout(() => navigate('/integrations'), 3000);
          return;
        }

        if (success === 'true') {
          setStatus('success');
          setMessage(
            integrationName
              ? `${integrationName} connected successfully!`
              : 'Integration connected successfully!'
          );
          setTimeout(() => navigate('/integrations', { replace: true }), 2000);
        } else {
          setStatus('error');
          setMessage('Integration connection failed');
          setTimeout(() => navigate('/integrations'), 3000);
        }
      } catch (error: any) {
        console.error('Integration callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Integration failed');
        setTimeout(() => navigate('/integrations'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-lg p-8">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {status === 'processing' && (
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-4 border-brand/20"></div>
                <div className="absolute inset-0 rounded-full border-4 border-brand border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FiLoader className="w-8 h-8 text-brand" />
                </div>
              </div>
            )}
            {status === 'success' && (
              <div className="w-20 h-20 bg-completed/10 rounded-full flex items-center justify-center">
                <FiCheckCircle className="w-10 h-10 text-completed" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-20 h-20 bg-blocked/10 rounded-full flex items-center justify-center">
                <FiAlertCircle className="w-10 h-10 text-blocked" />
              </div>
            )}
          </div>

          {/* Status Message */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-text-primary mb-2">
              {status === 'processing' && 'Connecting...'}
              {status === 'success' && 'Success!'}
              {status === 'error' && 'Connection Failed'}
            </h2>
            <p className="text-text-secondary">{message}</p>

            {status === 'error' && (
              <button
                onClick={() => navigate('/integrations')}
                className="mt-6 px-6 py-2 bg-brand text-white rounded-xl font-medium hover:bg-brand-dark transition-colors"
              >
                Return to Integrations
              </button>
            )}

            {status === 'success' && (
              <p className="mt-4 text-sm text-text-secondary">
                Redirecting to integrations page...
              </p>
            )}
          </div>

          {/* Loading Bar */}
          {status === 'processing' && (
            <div className="mt-8">
              <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-brand to-blue-400 animate-pulse"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
